import SearchIcon from '@mui/icons-material/Search'
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import Button from '@mui/material/Button'
import dayjs, { Dayjs } from 'dayjs'
import { useCallback, useState } from 'react'

import ClearIcon from '@mui/icons-material/Clear'
import DateRangePicker from '../../../../../components/DateRangePicker'
import ExportExcel from '../../../../../components/Export'
import CategorySelectSearch from '../../../../../components/SelectSearch/CategorySelectSearch'
import ChannelSelectSearch from '../../../../../components/SelectSearch/ChannelSelectSearch'
import ProductGroupSelectSearch from '../../../../../components/SelectSearch/ProductGroupSelectSearch'
import ProductSelectSearch from '../../../../../components/SelectSearch/ProductSelectSearch'
import {
  ImageLinks,
  LogoTypeProvider,
} from '../../../../../constants/image-link'
import useAxios from '../../../../../hooks/useAxios'
import { useUser } from '../../../../../hooks/useUser'
import { ICategory } from '../../../../../types/category'
import { IDistributionChannel } from '../../../../../types/distribution-channel'
import { PaginationResponseInterface } from '../../../../../types/pagination'
import { IProduct } from '../../../../../types/product'
import { AllowShippingProviderLists } from '../../../../../utils/provider'
import { EStatusOrderShowTracking } from '../../../../Order/components/OrderFilterDrawer'

export interface IFilterMapped {
  categoryObjectIds?: string[]
  distributionObjectIds?: string[]
  productObjectIds?: string[]
  productGroups?: string[]
  status?: string
  shippingCostDisplay?: string
  calTax?: string
  startDate?: string
  endDate?: string
  commissionType?: string
  trackingStatus?: string
  expense?: number
  codPercent?: number
  shippingCost?: number
  showAdsShipping?: number
  isAi?: string
  includeReturned?: string
}

interface FilterGroupProps {
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
  onDownload?: (date: Dayjs) => void
}

const menuItemsTypeOrderType = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'รายการทั่วไป',
    value: 'order',
  },
  {
    label: 'รายการจาก CRM',
    value: 'crm',
  },
  // {
  //   label: 'รายการจาก Telesale',
  //   value: 'telesale',
  // },
]

const menuItemsShippingCostDisplayType = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'แสดง',
    value: '1',
  },
  {
    label: 'ไม่แสดง',
    value: '0',
  },
]

const menuItemsCalTaxType = [
  {
    label: 'ไม่คำนวนภาษีและยอดตีกลับ',
    value: '0',
  },
  {
    label: 'คำนวนภาษีและยอดตีกลับ',
    value: '1',
  },
]

const menuItemsTypeStatus = [
  {
    label: 'ทั้งหมด',
    value: 'all',
  },
  {
    label: 'ส่งออกแล้ว',
    value: EStatusOrderShowTracking.SHIPPED,
  },
  {
    label: 'เฉพาะรายการที่มี Tracking',
    value: EStatusOrderShowTracking.TRACKING,
  },
  {
    label: 'ยังไม่มี Tracking',
    value: EStatusOrderShowTracking.NO_TRACKING,
  },
  {
    label: 'สินค้าค้างส่ง',
    value: EStatusOrderShowTracking.PENDING,
  },
]

const DailySalesReportFilter = (props: FilterGroupProps) => {
  const { filter, onFilterChange, onSearch, onDownload } = props
  const {
    startDate,
    endDate,
    productObjectIds,
    distributionObjectIds,
    categoryObjectIds,
    expense = 1,
    shippingCost = 1,
  } = filter
  const [searchProduct, setSearchProduct] = useState<string>('')
  const [searchCategory, setSearchCategory] = useState<string>('')
  const [searchDistributionChannel, setSearchDistributionChannel] =
    useState<string>('')
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')
  const { merchant } = useUser()

  const productsList = useAxios<PaginationResponseInterface<IProduct>>({
    method: 'GET',
    url: '/product',
    params: {
      page: 1,
      perPage: 5,
      search: searchProduct,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchProduct = async (value: string) => {
    setSearchProduct(value)
    await productsList.mutate()
  }

  const categoriesList = useAxios<PaginationResponseInterface<ICategory>>({
    method: 'GET',
    url: '/category',
    params: {
      page: 1,
      perPage: 5,
      search: searchCategory,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchCategory = async (value: string) => {
    setSearchCategory(value)
    await categoriesList.mutate()
  }

  const distributionChannelsList = useAxios<
    PaginationResponseInterface<IDistributionChannel>
  >({
    method: 'GET',
    url: '/distribution-channels',
    params: {
      page: 1,
      perPage: 5,
      search: searchDistributionChannel,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchDistributionChannel = async (value: string) => {
    setSearchDistributionChannel(value)
    try {
      await distributionChannelsList.mutate()
    } catch (error) {}
  }

  const handleFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      onFilterChange({ ...filter, ...newFilter })
    },
    [filter, onFilterChange],
  )

  const col = 2

  return (
    <Grid container spacing={2} mb={2}>
      <Grid item xs={12} lg={4}>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={(startDate, endDate) => {
            onFilterChange({ ...filter, startDate, endDate })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={8 / 3}>
        <ProductSelectSearch
          resetAutoComplete={resetAutoComplete}
          products={productObjectIds}
          handleFilterChange={handleFilterChange}
          onSelect={(e) => {
            const selectedProductIds = e?.map(
              (product: any) => product?.value?.objectId,
            )
            handleFilterChange({ productObjectIds: selectedProductIds })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={8 / 3}>
        <ProductGroupSelectSearch
          resetAutoComplete={resetAutoComplete}
          productGroups={filter.productGroups}
          onSelect={(e) => {
            const selectedGroupIds = e?.map((group) => group.value.objectId)
            handleFilterChange({ productGroups: selectedGroupIds })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={8 / 3}>
        {/* <SelectSearchMultiple<ICategory>
          resetAutoComplete={resetAutoComplete}
          placeholder={'เลือกหมวดหมู่สินค้า'}
          label={'เลือกหมวดหมู่สินค้า'}
          onSelect={(e) => {
            const selectedCategoryIds = e?.map(
              (category) => category?.value?.objectId,
            )
            onFilterChange({
              ...filter,
              categoryObjectIds: selectedCategoryIds,
            })
          }}
          search={searchCategory}
          onSearch={handleSearchCategory}
          options={
            categoriesList.data?.records
              .filter(
                (category: ICategory) =>
                  !categoryObjectIds?.includes(category.objectId),
              )
              .map(
                (category: ICategory) =>
                  category.isAvailable && {
                    label: category.name,
                    value: category,
                  },
              )
              .reduce((acc: IOptionSelect<ICategory>[], category) => {
                if (category) {
                  acc.push(category)
                }
                return acc
              }, []) || ([] as IOptionSelect<ICategory>[])
          }
        /> */}
        <CategorySelectSearch
          resetAutoComplete={resetAutoComplete}
          categories={categoryObjectIds}
          onSelect={(e) => {
            const selectedCategoryIds = e?.map(
              (category: any) => category?.value?.objectId,
            )
            onFilterChange({
              ...filter,
              categoryObjectIds: selectedCategoryIds,
            })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={col}>
        <ChannelSelectSearch
          resetAutoComplete={resetAutoComplete}
          label={'เลือกช่องทางการขาย'}
          onSelect={(e) => {
            const selectedChannelIds = e?.map(
              (channel: any) => channel?.value?.objectId,
            )
            onFilterChange({
              ...filter,
              distributionObjectIds: selectedChannelIds,
            })
          }}
        />
      </Grid>
      <Grid item xs={12} lg={col}>
        <TextField
          key={resetAutoComplete}
          sx={{ minWidth: 200 }}
          fullWidth
          label="เลือกประเภทค่าคอม"
          select
          onChange={(e) =>
            onFilterChange({
              ...filter,
              commissionType: e.target.value as string,
            })
          }
        >
          <MenuItem value={'all'}>ไม่เลือก</MenuItem>
          <MenuItem value={'commission'}>ค่าคอมต่อชิ้น</MenuItem>
          <MenuItem value={'commissionOrder'}>ค่าคอมต่อคำสั่งซื้อ</MenuItem>
          <MenuItem value={'commissionPercent'}>ค่าคอม %</MenuItem>
        </TextField>
      </Grid>

      <Grid item xs={12} lg={col}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="status-label">สถานะคำสั่งซื้อ</InputLabel>
          <Select
            key={resetAutoComplete}
            id="status"
            name="status"
            labelId="status-label"
            defaultValue={filter.status ?? undefined}
            onChange={(e) =>
              onFilterChange({ ...filter, status: e.target.value as string })
            }
            fullWidth
          >
            <MenuItem disabled value="" selected>
              <em>สถานะคำสั่งซื้อ</em>
            </MenuItem>
            {menuItemsTypeOrderType
              .filter((item) =>
                !merchant.config?.openCRM ? item.value !== 'crm' : true,
              )
              .map((item, index: number) => (
                <MenuItem value={item.value} key={index}>
                  {item.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={col}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="shippingCostDisplay-label">เลือกขนส่ง</InputLabel>
          <Select
            key={resetAutoComplete}
            id="shippingCostDisplay"
            name="shippingCostDisplay"
            labelId="shippingCostDisplay-label"
            defaultValue={filter.shippingCostDisplay ?? undefined}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                shippingCostDisplay: e.target.value as string,
              })
            }
            fullWidth
          >
            <MenuItem disabled value="" selected>
              <em>การแสดงผลค่าขนส่ง</em>
            </MenuItem>
            <MenuItem value="all" key="all">
              ทั้งหมด
            </MenuItem>
            {AllowShippingProviderLists(merchant).map((item, index: number) => (
              <MenuItem value={item.value} key={index}>
                {item.value !== 'all' && (
                  <img
                    style={{ marginRight: 5 }}
                    src={
                      ImageLinks[
                        `LOGO_${item.value.toUpperCase()}` as LogoTypeProvider
                      ]
                    }
                    alt="smallLogoSocial"
                    height={20}
                  />
                )}
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={col}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="calTax-label">การคำนวนภาษีและยอดตีกลับ</InputLabel>
          <Select
            key={resetAutoComplete}
            id="calTax"
            name="calTax"
            labelId="calTax-label"
            defaultValue={filter.calTax ?? undefined}
            onChange={(e) =>
              onFilterChange({ ...filter, calTax: e.target.value as string })
            }
            fullWidth
          >
            <MenuItem disabled value="" selected>
              <em>การคำนวนภาษีและยอดตีกลับ</em>
            </MenuItem>
            {menuItemsCalTaxType.map((item, index: number) => (
              <MenuItem value={item.value} key={index}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={col}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="statusOrderShowTracking-label">สถานะ</InputLabel>
          <Select
            id="statusOrderShowTracking"
            name="statusOrderShowTracking"
            labelId="statusOrderShowTracking-label"
            fullWidth
            value={filter.trackingStatus || 'all'}
            onChange={(e) => {
              onFilterChange({
                ...filter,
                trackingStatus: e.target.value as string,
              })
            }}
          >
            {menuItemsTypeStatus.map((item, index: number) => (
              <MenuItem value={item.value} key={index}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={col}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="calTax-label">ค่าใช้จ่าย</InputLabel>
          <Select
            key={resetAutoComplete}
            id="expense"
            name="expense"
            labelId="expense-label"
            defaultValue={filter.expense ?? 1}
            onChange={(e) =>
              onFilterChange({ ...filter, expense: e.target.value as number })
            }
            fullWidth
          >
            <MenuItem value={1}>รวม</MenuItem>
            <MenuItem value={0}>ไม่รวม</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={col}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="shippingCost-label">ค่าส่ง</InputLabel>
          <Select
            key={resetAutoComplete}
            id="shippingCost"
            name="shippingCost"
            labelId="shippingCost-label"
            defaultValue={filter.shippingCost ?? 1}
            onChange={(e) => {
              const newFilter = {
                ...filter,
                shippingCost: e.target.value as number,
              }
              onFilterChange(newFilter)
              onSearch(newFilter)
            }}
            fullWidth
          >
            <MenuItem value={1}>รวม</MenuItem>
            <MenuItem value={0}>ไม่รวม</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={col}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="calTax-label">% COD</InputLabel>
          <Select
            key={resetAutoComplete}
            id="codPercent"
            name="codPercent"
            labelId="codPercent-label"
            defaultValue={filter.codPercent ?? 0}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                codPercent: e.target.value as number,
              })
            }
            fullWidth
          >
            <MenuItem value={1}>รวม</MenuItem>
            <MenuItem value={0}>ไม่รวม</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={col}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="calTax-label">แสดงค่า Ads และค่าขนส่ง</InputLabel>
          <Select
            key={resetAutoComplete}
            id="showAdsShipping"
            name="showAdsShipping"
            labelId="showAdsShipping-label"
            defaultValue={filter.showAdsShipping ?? 0}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                showAdsShipping: e.target.value as number,
              })
            }
            fullWidth
          >
            <MenuItem value={1}>แสดง</MenuItem>
            <MenuItem value={0}>ไม่แสดง</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={col}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="calTax-label">แสดงคำสั่งซื้อ AI</InputLabel>
          <Select
            key={resetAutoComplete}
            id="isAi"
            name="isAi"
            labelId="isAi-label"
            defaultValue={filter.isAi ?? ''}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                isAi: e.target.value,
              })
            }
            fullWidth
          >
            <MenuItem value={'all'}>ทั้งหมด</MenuItem>
            <MenuItem value={'true'}>เฉพาะ AI</MenuItem>
            {/* <MenuItem value={'false'}>ไม่ใช่ AI</MenuItem> */}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={col}>
        <FormControl variant="filled" fullWidth>
          <InputLabel id="includeReturned-label">
            ตีกลับ (เซ็นรับแล้ว)
          </InputLabel>
          <Select
            key={resetAutoComplete}
            id="includeReturned"
            name="includeReturned"
            labelId="includeReturned-label"
            defaultValue={filter.includeReturned ?? 'false'}
            onChange={(e) =>
              onFilterChange({
                ...filter,
                includeReturned: e.target.value,
              })
            }
            fullWidth
          >
            <MenuItem value={'false'}>ไม่รวม</MenuItem>
            <MenuItem value={'true'}>รวม</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={2}>
        <Button
          color="primary"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => onSearch(filter)}
          sx={{ height: '100%' }}
        >
          ค้นหา
        </Button>
      </Grid>
      <Grid item xs={4} lg={2}>
        <Button
          color="inherit"
          size="large"
          fullWidth
          variant="contained"
          startIcon={<ClearIcon />}
          onClick={() => {
            onFilterChange({
              categoryObjectIds: undefined,
              distributionObjectIds: undefined,
              productObjectIds: undefined,
              productGroups: undefined,
              status: undefined,
              shippingCostDisplay: undefined,
              calTax: undefined,
              startDate: dayjs().subtract(30, 'days').format(),
              endDate: dayjs().format(),
            })
            onSearch({
              categoryObjectIds: undefined,
              distributionObjectIds: undefined,
              productObjectIds: undefined,
              productGroups: undefined,
              status: undefined,
              shippingCostDisplay: undefined,
              calTax: undefined,
              startDate: dayjs().subtract(30, 'days').format(),
              endDate: dayjs().format(),
            })
            setResetAutoComplete(new Date().getTime().toString())
          }}
          sx={{ height: '100%' }}
        >
          รีเซ็ต
        </Button>
      </Grid>
      <Grid item xs={12} lg={2}>
        <ExportExcel onDownload={onDownload} filterDate={false} />
      </Grid>
    </Grid>
  )
}

export default DailySalesReportFilter
